import { useState, useEffect } from "react";
import { TEMPLATES, ANIMATION_TYPES, ANIMATION_FREQUENCIES, ONE_SHOT_ANIMATIONS } from "../constants";
import HelpTooltip from "./HelpTooltip";

const FEATURE_LABELS = [
  { key: "animation", label: "Animation", icon: "bi-stars" },
  { key: "rotatingMessages", label: "Rotating Messages", icon: "bi-chat-left-text" },
  { key: "avatar", label: "Avatar", icon: "bi-person-circle" },
  { key: "notificationBadge", label: "Notification Badge", icon: "bi-bell" },
];

const DISPLAY_STYLES = [
  { id: "rotating", label: "Single Rotating", icon: "bi-arrow-repeat", description: "One message at a time, cycling through" },
  { id: "stacked", label: "Stacked Sequential", icon: "bi-chat-left-text", description: "Messages appear one after another" },
];

const RotatingText = ({ messages, interval }: any) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const timer = setInterval(() => {
      setIdx((prev) => (prev + 1) % messages.length);
    }, (interval || 4) * 1000);
    return () => clearInterval(timer);
  }, [messages, interval]);
  if (!messages || messages.length === 0) return null;
  return <span className="wc-tmpl-rotating-text">{messages[idx]}</span>;
};

const templatePreviews = {
  floating: (colors, welcomeMsgs) => (
    <div className="wc-tmpl-preview-scene">
      <div className="wc-tmpl-browser-bar">
        <span className="wc-tmpl-dot red" />
        <span className="wc-tmpl-dot yellow" />
        <span className="wc-tmpl-dot green" />
        <span className="wc-tmpl-url">hotel-website.com</span>
      </div>
      <div className="wc-tmpl-browser-body">
        <div className="wc-tmpl-page-lines">
          <div className="wc-tmpl-line w-75" />
          <div className="wc-tmpl-line w-50" />
          <div className="wc-tmpl-line w-60" />
        </div>
        {/* Rotating message bubble */}
        <div className="wc-tmpl-rotating-bubble">
          <RotatingText messages={welcomeMsgs} interval={4} />
        </div>
        {/* Floating icon + badge */}
        <div className="wc-tmpl-floating-group">
          <div
            className="wc-tmpl-floating-icon wc-anim-pulse"
            style={{ backgroundColor: colors.primary }}
          >
            <i className="bi bi-chat-dots-fill" style={{ color: colors.text }} />
          </div>

        </div>
      </div>
    </div>
  ),

  promo_card: (colors) => (
    <div className="wc-tmpl-preview-scene">
      <div className="wc-tmpl-browser-bar">
        <span className="wc-tmpl-dot red" />
        <span className="wc-tmpl-dot yellow" />
        <span className="wc-tmpl-dot green" />
        <span className="wc-tmpl-url">hotel-website.com</span>
      </div>
      <div className="wc-tmpl-browser-body">
        <div className="wc-tmpl-promo-card-v2">
          <div className="wc-tmpl-promo-banner" style={{ backgroundColor: colors.primary }}>
            <span className="wc-tmpl-promo-discount">20% OFF</span>
            <span className="wc-tmpl-promo-badge-tag">Limited Time</span>
          </div>
          <div className="wc-tmpl-promo-content">
            <div className="wc-tmpl-promo-title-v2">Weekend Getaway Package</div>
            <div className="wc-tmpl-promo-highlights">
              <span className="wc-tmpl-promo-highlight-item"><i className="bi bi-check-circle-fill" /> Breakfast included</span>
              <span className="wc-tmpl-promo-highlight-item"><i className="bi bi-check-circle-fill" /> Free spa access</span>
            </div>
            <div className="wc-tmpl-promo-price">
              <span className="wc-tmpl-promo-old-price">$299</span>
              <span className="wc-tmpl-promo-new-price" style={{ color: colors.primary }}>$239</span>
            </div>
            <button type="button"
              className="wc-tmpl-promo-cta-v2"
              style={{ backgroundColor: colors.primary, color: colors.text }}
            >
              Grab This Deal
            </button>
          </div>
        </div>
      </div>
    </div>
  ),

  assistant_bubble: (colors) => (
    <div className="wc-tmpl-preview-scene">
      <div className="wc-tmpl-browser-bar">
        <span className="wc-tmpl-dot red" />
        <span className="wc-tmpl-dot yellow" />
        <span className="wc-tmpl-dot green" />
        <span className="wc-tmpl-url">hotel-website.com</span>
      </div>
      <div className="wc-tmpl-browser-body">
        <div className="wc-tmpl-page-lines">
          <div className="wc-tmpl-line w-75" />
          <div className="wc-tmpl-line w-50" />
        </div>
        {/* Premium assistant card */}
        <div className="wc-tmpl-assistant-card-v2">
          <div className="wc-tmpl-assistant-card-body">
            <div className="wc-tmpl-assistant-identity">
              <span className="wc-tmpl-assistant-name-v2" style={{ color: colors.primary }}>Emma</span>
              <span className="wc-tmpl-assistant-role">· Guest Relations</span>
            </div>
            <p className="wc-tmpl-assistant-greeting">
              Ask me about rooms,<br />dining and offers.
            </p>
            <button type="button"
              className="wc-tmpl-assistant-cta"
              style={{ backgroundColor: colors.primary, color: colors.text }}
            >
              Chat Now
            </button>
          </div>
          {/* Avatar at bottom */}
          <div className="wc-tmpl-assistant-avatar-v2" style={{ borderColor: colors.primary }}>
            <div className="wc-tmpl-assistant-avatar-inner" style={{ backgroundColor: colors.secondary }}>
              <i className="bi bi-person-fill" style={{ color: colors.primary, fontSize: "14px" }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  ),

  sticky_bar: (colors) => (
    <div className="wc-tmpl-preview-scene">
      <div className="wc-tmpl-browser-bar">
        <span className="wc-tmpl-dot red" />
        <span className="wc-tmpl-dot yellow" />
        <span className="wc-tmpl-dot green" />
        <span className="wc-tmpl-url">hotel-website.com</span>
      </div>
      <div className="wc-tmpl-browser-body wc-tmpl-body-tall">
        <div className="wc-tmpl-page-lines">
          <div className="wc-tmpl-line w-75" />
          <div className="wc-tmpl-line w-50" />
          <div className="wc-tmpl-line w-60" />
        </div>
        <div
          className="wc-tmpl-sticky-bar"
          style={{ backgroundColor: colors.primary, color: colors.text }}
        >
          <span>Need help planning your stay?</span>
          <span className="wc-tmpl-sticky-cta">Chat Now →</span>
        </div>
      </div>
    </div>
  ),

  concierge: (colors) => (
    <div className="wc-tmpl-preview-scene">
      <div className="wc-tmpl-browser-bar">
        <span className="wc-tmpl-dot red" />
        <span className="wc-tmpl-dot yellow" />
        <span className="wc-tmpl-dot green" />
        <span className="wc-tmpl-url">hotel-website.com</span>
      </div>
      <div className="wc-tmpl-browser-body">
        <div className="wc-tmpl-page-lines">
          <div className="wc-tmpl-line w-75" />
        </div>
        {/* Premium concierge card — luxury style */}
        <div className="wc-tmpl-concierge-card-v2">
          <div
            className="wc-tmpl-concierge-header-v2"
            style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
          >
            <div className="wc-tmpl-concierge-avatar-wrap">
              <div className="wc-tmpl-concierge-avatar">
                <i className="bi bi-person-fill" style={{ color: "#fff", fontSize: "12px" }} />
              </div>
              <span className="wc-tmpl-concierge-status-dot" />
            </div>
            <div className="wc-tmpl-concierge-title-area">
              <span className="wc-tmpl-concierge-title" style={{ color: "#fff" }}>Personal Concierge</span>
              <span className="wc-tmpl-concierge-subtitle" style={{ color: "rgba(255,255,255,0.8)" }}>At your service</span>
            </div>
          </div>
          <div className="wc-tmpl-concierge-body-v2">
            <p className="wc-tmpl-concierge-welcome">Good evening. How may I assist you today?</p>
            <div className="wc-tmpl-concierge-services">
              <span className="wc-tmpl-concierge-service-tag">Room Service</span>
              <span className="wc-tmpl-concierge-service-tag">Spa</span>
              <span className="wc-tmpl-concierge-service-tag">Dining</span>
            </div>
            <button type="button"
              className="wc-tmpl-concierge-btn-v2"
              style={{ backgroundColor: colors.primary, color: colors.text }}
            >
              <i className="bi bi-bell-fill" style={{ fontSize: "8px", marginRight: "3px" }} />
              Request Concierge
            </button>
          </div>
          <div className="wc-tmpl-concierge-gold-accent" style={{ backgroundColor: colors.secondary }} />
        </div>
      </div>
    </div>
  ),
};

/* ===== Floating Chat Icon — Customize Rotating Messages ===== */
const FloatingContentEditor = ({ config, onChange }: any) => {
  const templateContent = config.templateContent || {};
  const floating = templateContent.floating || {};
  const messages = floating.messages || [];
  const displayStyle = floating.displayStyle || "rotating";
  const [newMsg, setNewMsg] = useState("");

  const updateFloating = (updates) => {
    onChange("templateContent", {
      ...templateContent,
      floating: { ...floating, ...updates },
    });
  };

  const addMessage = () => {
    const trimmed = newMsg.trim();
    if (!trimmed) return;
    updateFloating({ messages: [...messages, trimmed] });
    setNewMsg("");
  };

  const removeMessage = (idx) => {
    updateFloating({ messages: messages.filter((_, i) => i !== idx) });
  };

  const moveMessage = (idx, direction) => {
    const newMessages = [...messages];
    const target = idx + direction;
    if (target < 0 || target >= newMessages.length) return;
    [newMessages[idx], newMessages[target]] = [newMessages[target], newMessages[idx]];
    updateFloating({ messages: newMessages });
  };

  const updateMessage = (idx, value) => {
    const newMessages = [...messages];
    newMessages[idx] = value;
    updateFloating({ messages: newMessages });
  };

  return (
    <div className="wc-section">
      <div className="wc-section-header">
        <div>
          <h5 className="wc-section-title">
            <i className="bi bi-chat-left-text me-2" />
            Chat Icon Messages
            <HelpTooltip text="These are the short messages that appear next to your chat icon to catch visitors' attention and encourage them to start a conversation." />
          </h5>
          <p className="wc-section-subtitle">
            Edit the messages shown near your chat icon — changes appear in the live preview instantly
          </p>
        </div>
      </div>

      {/* Display Style Selection */}
      <div className="wc-subsection">
        <h6 className="wc-subsection-title">How Messages Appear</h6>
        <p className="wc-hint">Choose how messages are shown next to the chat icon</p>
        <div className="wc-display-style-grid">
          {DISPLAY_STYLES.map((style) => (
            <div
              key={style.id}
              className={`wc-display-style-card ${displayStyle === style.id ? "wc-display-style-card--active" : ""}`}
              onClick={() => updateFloating({ displayStyle: style.id })}
            >
              <i className={`bi ${style.icon} wc-display-style-icon`} />
              <div className="wc-display-style-info">
                <span className="wc-display-style-label">{style.label}</span>
                <span className="wc-display-style-desc">{style.description}</span>
              </div>
              {displayStyle === style.id && (
                <i className="bi bi-check-circle-fill wc-display-style-check" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Messages List */}
      <div className="wc-subsection">
        <h6 className="wc-subsection-title">
          Messages
          <span className="wc-hint ms-2">
            {displayStyle === "stacked"
              ? "Messages appear one after another in bubbles"
              : "Messages cycle one at a time"}
          </span>
        </h6>
        <div className="wc-rotating-messages-list">
          {messages.map((msg, idx) => (
            <div key={idx} className="wc-rotating-msg-item">
              <span className="wc-rotating-msg-number">{idx + 1}</span>
              <input
                type="text"
                className="form-control form-control-sm wc-rotating-msg-input"
                value={msg}
                onChange={(e) => updateMessage(idx, e.target.value)}
              />
              <div className="wc-rotating-msg-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveMessage(idx, -1)}
                  disabled={idx === 0}
                  title="Move up"
                >
                  <i className="bi bi-arrow-up" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveMessage(idx, 1)}
                  disabled={idx === messages.length - 1}
                  title="Move down"
                >
                  <i className="bi bi-arrow-down" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeMessage(idx)}
                  title="Remove"
                >
                  <i className="bi bi-trash" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="wc-rotating-msg-add mt-2">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Add a new message..."
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addMessage()}
          />
          <button type="button" className="btn btn-sm btn-outline-primary ms-2" onClick={addMessage}>
            <i className="bi bi-plus-lg" />
          </button>
        </div>

        {/* Suggestion Chips */}
        <div className="wc-suggestion-chips mt-3">
          <span className="wc-hint me-2">Quick add:</span>
          {["Looking for rooms?", "Book direct & save!", "AI-powered booking assistant", "Check room availability", "Best rate guaranteed!"].map((suggestion) => (
            <button
              type="button"
              key={suggestion}
              className="wc-suggestion-chip"
              onClick={() => {
                if (!messages.includes(suggestion)) {
                  updateFloating({ messages: [...messages, suggestion] });
                }
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

/* ===== Promotional Card — Content Editor ===== */
const PromoCardContentEditor = ({ config, onChange }: any) => {
  const templateContent = config.templateContent || {};
  const promo = templateContent.promo_card || {};

  const updatePromo = (updates) => {
    onChange("templateContent", {
      ...templateContent,
      promo_card: { ...promo, ...updates },
    });
  };

  return (
    <div className="wc-section">
      <div className="wc-section-header">
        <div>
          <h5 className="wc-section-title">
            <i className="bi bi-card-heading me-2" />
            Offer Card Content
            <HelpTooltip text="This card highlights a special deal or promotion on your website. Visitors see it before opening the chatbot. Edit the text below and see changes in the preview." />
          </h5>
          <p className="wc-section-subtitle">
            Set the headline, description, and button text for your offer card — changes appear in the live preview instantly
          </p>
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Title</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Weekend Getaway Package"
          value={promo.title || ""}
          onChange={(e) => updatePromo({ title: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-2">
          {["Book Direct & Save 20%", "AI-Recommended Room Deals", "Exclusive Direct Booking Offer"].map((ex) => (
            <button type="button" key={ex} className="wc-suggestion-chip" onClick={() => updatePromo({ title: ex })}>
              {ex}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Description</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Book a suite this weekend and save big"
          value={promo.description || ""}
          onChange={(e) => updatePromo({ description: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-2">
          {["Let our AI find you the best room deal", "Book direct for exclusive rates & perks", "Best price guaranteed on direct bookings"].map((ex) => (
            <button type="button" key={ex} className="wc-suggestion-chip" onClick={() => updatePromo({ description: ex })}>
              {ex}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Button Label</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Grab This Deal"
          value={promo.buttonText || ""}
          onChange={(e) => updatePromo({ buttonText: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-2">
          {["Book Direct Now", "Let AI Find My Room", "Check Availability", "Get Best Rate"].map((ex) => (
            <button type="button" key={ex} className="wc-suggestion-chip" onClick={() => updatePromo({ buttonText: ex })}>
              {ex}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

/* ===== Guest Relations Assistant — Content Editor ===== */
const AssistantContentEditor = ({ config, onChange }: any) => {
  const templateContent = config.templateContent || {};
  const assistant = templateContent.assistant_bubble || {};

  const updateAssistant = (updates) => {
    onChange("templateContent", {
      ...templateContent,
      assistant_bubble: { ...assistant, ...updates },
    });
  };

  return (
    <div className="wc-section">
      <div className="wc-section-header">
        <div>
          <h5 className="wc-section-title">
            <i className="bi bi-person-badge me-2" />
            Virtual Assistant Details
            <HelpTooltip text="This sets up the virtual assistant card that greets visitors on your website. Customise the name, role, and greeting message your guests will see." />
          </h5>
          <p className="wc-section-subtitle">
            Personalise the assistant your guests see — edit any field and the live preview updates instantly
          </p>
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Assistant Name</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Emma"
          value={assistant.agentName || ""}
          onChange={(e) => updateAssistant({ agentName: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-2">
          {["AI Booking Assistant", "AI Concierge", "AI Room Advisor", "AI Guest Helper"].map((ex) => (
            <button type="button" key={ex} className="wc-suggestion-chip" onClick={() => updateAssistant({ agentName: ex })}>
              {ex}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Role</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Guest Relations"
          value={assistant.role || ""}
          onChange={(e) => updateAssistant({ role: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-2">
          {["AI Booking Assistant", "AI Room Advisor", "AI Reservations", "AI Guest Services"].map((ex) => (
            <button type="button" key={ex} className="wc-suggestion-chip" onClick={() => updateAssistant({ role: ex })}>
              {ex}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Welcome Greeting</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Ask me about rooms, dining and offers."
          value={assistant.greeting || ""}
          onChange={(e) => updateAssistant({ greeting: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-2">
          {["Ask me to find rooms, rates & availability.", "Let AI help you book the perfect room.", "I can compare rates & find you the best deal."].map((ex) => (
            <button type="button" key={ex} className="wc-suggestion-chip" onClick={() => updateAssistant({ greeting: ex })}>
              {ex}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Button Label</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Chat Now"
          value={assistant.buttonText || ""}
          onChange={(e) => updateAssistant({ buttonText: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-2">
          {["Book with AI", "Find My Room", "Check Rates", "Ask AI Assistant"].map((ex) => (
            <button type="button" key={ex} className="wc-suggestion-chip" onClick={() => updateAssistant({ buttonText: ex })}>
              {ex}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

/* ===== Sticky Bar — Content Editor ===== */
const STICKY_MESSAGE_SUGGESTIONS = [
  "Need help planning your stay?",
  "Chat with us for exclusive deals!",
  "Questions? We're here to help",
  "Book direct and save!",
];
const STICKY_BUTTON_SUGGESTIONS = [
  "Chat Now",
  "Get Help",
  "Book Now",
  "Ask Us",
];

const StickyBarContentEditor = ({ config, onChange }: any) => {
  const templateContent = config.templateContent || {};
  const sticky = templateContent.sticky_bar || {};

  const updateSticky = (updates) => {
    onChange("templateContent", {
      ...templateContent,
      sticky_bar: { ...sticky, ...updates },
    });
  };

  return (
    <div className="wc-section">
      <div className="wc-section-header">
        <div>
          <h5 className="wc-section-title">
            <i className="bi bi-layout-text-window-reverse me-2" />
            Bottom Bar Content
            <HelpTooltip text="The bottom bar is a strip fixed at the bottom of your website that invites visitors to chat. Edit the message and button label below." />
          </h5>
          <p className="wc-section-subtitle">
            Edit the text shown in the bar at the bottom of your website — changes appear in the live preview instantly
          </p>
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Display Message</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Need help planning your stay?"
          value={sticky.message || ""}
          onChange={(e) => updateSticky({ message: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-1">
          {STICKY_MESSAGE_SUGGESTIONS.map((s, i) => (
            <button type="button" key={i} className="wc-suggestion-chip" onClick={() => updateSticky({ message: s })}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Button Label</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Chat Now"
          value={sticky.buttonText || ""}
          onChange={(e) => updateSticky({ buttonText: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-1">
          {STICKY_BUTTON_SUGGESTIONS.map((s, i) => (
            <button type="button" key={i} className="wc-suggestion-chip" onClick={() => updateSticky({ buttonText: s })}>
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ===== Premium Concierge — Content Editor ===== */
const CONCIERGE_TITLE_SUGGESTIONS = [
  "Personal Concierge",
  "Guest Relations",
  "VIP Concierge",
  "Hotel Concierge",
];
const CONCIERGE_GREETING_SUGGESTIONS = [
  "Good evening. How may I assist you today?",
  "Welcome! How can I make your stay special?",
  "Hello! What can I help you with?",
];
const CONCIERGE_SERVICE_SUGGESTIONS = [
  "Room Service",
  "Spa & Wellness",
  "Dining",
  "Airport Transfer",
  "Housekeeping",
  "Tours & Activities",
  "Laundry",
  "Wake-up Call",
];

const ConciergeContentEditor = ({ config, onChange }: any) => {
  const templateContent = config.templateContent || {};
  const concierge = templateContent.concierge || {};
  const services = concierge.services || [];
  const [newService, setNewService] = useState("");

  const updateConcierge = (updates) => {
    onChange("templateContent", {
      ...templateContent,
      concierge: { ...concierge, ...updates },
    });
  };

  const addService = () => {
    const trimmed = newService.trim();
    if (!trimmed) return;
    if (services.includes(trimmed)) return;
    updateConcierge({ services: [...services, trimmed] });
    setNewService("");
  };

  const removeService = (idx) => {
    updateConcierge({ services: services.filter((_, i) => i !== idx) });
  };

  const addSuggestedService = (svc) => {
    if (services.includes(svc)) return;
    updateConcierge({ services: [...services, svc] });
  };

  return (
    <div className="wc-section">
      <div className="wc-section-header">
        <div>
          <h5 className="wc-section-title">
            <i className="bi bi-gem me-2" />
            Concierge Card Details
            <HelpTooltip text="The concierge card gives your website a premium feel. It shows a personalised greeting and a list of services. Edit the content below to match your hotel's offerings." />
          </h5>
          <p className="wc-section-subtitle">
            Personalise the concierge card your guests see — edit any field and the live preview updates instantly
          </p>
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Title</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Personal Concierge"
          value={concierge.title || ""}
          onChange={(e) => updateConcierge({ title: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-1">
          {CONCIERGE_TITLE_SUGGESTIONS.map((s, i) => (
            <button type="button" key={i} className="wc-suggestion-chip" onClick={() => updateConcierge({ title: s })}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Subtitle</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. At your service"
          value={concierge.subtitle || ""}
          onChange={(e) => updateConcierge({ subtitle: e.target.value })}
        />
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Greeting</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Good evening. How may I assist you today?"
          value={concierge.greeting || ""}
          onChange={(e) => updateConcierge({ greeting: e.target.value })}
        />
        <div className="wc-suggestion-chips mt-1">
          {CONCIERGE_GREETING_SUGGESTIONS.map((s, i) => (
            <button type="button" key={i} className="wc-suggestion-chip" onClick={() => updateConcierge({ greeting: s })}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Service Tags</h6>
        <div className="wc-service-tags-list">
          {services.map((svc, idx) => (
            <span key={idx} className="wc-service-tag-item">
              {svc}
              <button type="button" className="wc-service-tag-remove" onClick={() => removeService(idx)}>
                <i className="bi bi-x" />
              </button>
            </span>
          ))}
        </div>
        <div className="wc-rotating-msg-add mt-2">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Add a service tag..."
            value={newService}
            onChange={(e) => setNewService(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addService()}
          />
          <button type="button" className="btn btn-sm btn-outline-primary ms-2" onClick={addService}>
            <i className="bi bi-plus-lg" />
          </button>
        </div>
        <div className="wc-suggestion-chips mt-2">
          {CONCIERGE_SERVICE_SUGGESTIONS.filter((s) => !services.includes(s)).map((s, i) => (
            <button type="button" key={i} className="wc-suggestion-chip" onClick={() => addSuggestedService(s)}>
              + {s}
            </button>
          ))}
        </div>
      </div>

      <div className="wc-subsection">
        <h6 className="wc-subsection-title">Button Label</h6>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. Request Concierge"
          value={concierge.buttonText || ""}
          onChange={(e) => updateConcierge({ buttonText: e.target.value })}
        />
      </div>
    </div>
  );
};

const TemplateLibrary = ({ config, onChange }: any) => {
  const selected = config.template || "floating";
  const animation = config.animation || {};
  const selectedTemplate = TEMPLATES.find((t) => t.id === selected);
  const supportsAnimation = selectedTemplate?.features?.animation ?? false;
  const [activeTab, setActiveTab] = useState("templates");

  const colors = {
    primary: config.chatTheme?.chatprimaryBg || config.appearance?.primaryColor || "#9b4598",
    secondary: config.appearance?.secondaryColor || "#e1bee7",
    text: config.chatTheme?.chatprimaryText || "#ffffff",
  };
  const welcomeMsgs = config.welcome?.typingMessages || [
    "Looking for rooms?",
    "Book direct & save!",
    "AI assistant at your service",
  ];

  const handleAnimationChange = (key, value) => {
    const updates = { ...animation, [key]: value };
    if (key === "type" && ONE_SHOT_ANIMATIONS.includes(value)) {
      updates.frequency = "5s";
    }
    onChange("animation", updates);
  };

  const contentTabLabel = "Edit Content";

  const renderContentEditor = () => {
    switch (selected) {
      case "floating":
        return <FloatingContentEditor config={config} onChange={onChange} />;
      case "promo_card":
        return <PromoCardContentEditor config={config} onChange={onChange} />;
      case "assistant_bubble":
        return <AssistantContentEditor config={config} onChange={onChange} />;
      case "sticky_bar":
        return <StickyBarContentEditor config={config} onChange={onChange} />;
      case "concierge":
        return <ConciergeContentEditor config={config} onChange={onChange} />;
      default:
        return null;
    }
  };

  return (
    <div className="wc-section">
      {/* Sub-tabs */}
      <div className="wc-sub-tabs">
        <button
          type="button"
          className={`wc-sub-tab ${activeTab === "templates" ? "wc-sub-tab--active" : ""}`}
          onClick={() => setActiveTab("templates")}
        >
          <i className="bi bi-grid-3x3-gap me-1" />
          Choose Layout
        </button>
        <span className="wc-sub-tab-with-tooltip">
          <button
            type="button"
            className={`wc-sub-tab ${activeTab === "content" ? "wc-sub-tab--active" : ""}`}
            onClick={() => setActiveTab("content")}
          >
            <i className="bi bi-pencil-square me-1" />
            {contentTabLabel}
          </button>
          <span className="wc-sub-tab-tooltip">
            Any text or settings you change here will be displayed directly in your chatbot widget
          </span>
        </span>
      </div>

      {activeTab === "templates" ? (
        <>
          {/* ===== Templates Section ===== */}
          <div className="wc-section-header">
            <div>
              <h5 className="wc-section-title">
                <i className="bi bi-grid-3x3-gap me-2" />
                Choose Your Widget Layout
                <HelpTooltip text="Pick a layout that suits your website. Each layout controls how the chatbot appears to your visitors — you can always switch later." />
              </h5>
              <p className="wc-section-subtitle">
                Select a ready-made design for how your chatbot appears on your website
              </p>
            </div>
          </div>

          <div className="wc-template-grid">
            {TEMPLATES.map((tmpl) => {
              const isActive = selected === tmpl.id;
              const supportedFeatures = tmpl.features
                ? FEATURE_LABELS.filter((fl) => tmpl.features[fl.key]).map((fl) => fl.label)
                : [];
              return (
                <div
                  key={tmpl.id}
                  className={`wc-template-card ${isActive ? "wc-template-card--active" : ""}`}
                  onClick={() => onChange("template", tmpl.id)}
                >
                  <div className="wc-template-badge">{tmpl.category}</div>
                  <div className="wc-template-preview-wrap">
                    {templatePreviews[tmpl.id]
                      ? templatePreviews[tmpl.id](colors, welcomeMsgs)
                      : null}
                  </div>
                  <div className="wc-template-info">
                    <h6 className="wc-template-name">{tmpl.name}</h6>
                    <p className="wc-template-desc">{tmpl.description}</p>
                  </div>

                  {supportedFeatures.length > 0 && (
                    <div className="wc-template-hover-features">
                      {supportedFeatures.map((f) => (
                        <span key={f} className="wc-template-hover-feature-tag">{f}</span>
                      ))}
                    </div>
                  )}

                  <div className="wc-template-actions">
                    <button type="button"
                      className={`btn btn-sm ${isActive ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange("template", tmpl.id);
                      }}
                    >
                      {isActive ? (
                        <>
                          <i className="bi bi-check-circle me-1" />
                          Selected
                        </>
                      ) : (
                        "Select"
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===== Animation Controls (inline below templates) ===== */}
          <div className="wc-section-divider" />

          <div className="wc-section-header">
            <div>
              <h5 className="wc-section-title">
                <i className="bi bi-stars me-2" />
                Eye-Catching Effects
                <HelpTooltip text="Animations draw visitors' attention to the chat widget. Choose an effect and how often it plays — you can preview it live." />
              </h5>
              <p className="wc-section-subtitle">
                {supportsAnimation
                  ? `Add motion effects to "${selectedTemplate?.name}" to attract visitor attention`
                  : `"${selectedTemplate?.name}" does not support motion effects`}
              </p>
            </div>
          </div>

          {supportsAnimation ? (
            <>
              {/* Enable Toggle */}
              <div className="wc-subsection">
                <div className="wc-toggle-row">
                  <div>
                    <h6 className="wc-subsection-title mb-0">Enable Motion Effects</h6>
                    <p className="wc-hint">Add subtle movement to your widget to catch visitors' eyes</p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={animation.enabled !== false}
                      onChange={(e) => handleAnimationChange("enabled", e.target.checked)}
                      role="switch"
                    />
                  </div>
                </div>
              </div>

              {animation.enabled !== false && (
                <>
                  {/* Animation Type */}
                  <div className="wc-subsection">
                    <h6 className="wc-subsection-title">Effect Style</h6>
                    <div className="wc-animation-type-grid">
                      {ANIMATION_TYPES.map((a) => (
                        <div
                          key={a.id}
                          className={`wc-animation-type-card ${animation.type === a.id ? "wc-animation-type-card--active" : ""}`}
                          onClick={() => handleAnimationChange("type", a.id)}
                        >
                          <span className="wc-animation-type-label">{a.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Animation Frequency */}
                  <div className="wc-subsection">
                    <h6 className="wc-subsection-title">How Often</h6>
                    <div className="wc-frequency-options">
                      {ANIMATION_FREQUENCIES
                        .filter((f) => !(ONE_SHOT_ANIMATIONS.includes(animation.type) && f.id === "continuous"))
                        .map((f) => (
                        <label key={f.id} className="wc-radio-card">
                          <input
                            type="radio"
                            name="animFrequency"
                            value={f.id}
                            checked={animation.frequency === f.id}
                            onChange={() => handleAnimationChange("frequency", f.id)}
                          />
                          <span className="wc-radio-card-label">{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="wc-animation-not-available">
              <i className="bi bi-info-circle me-2" />
              Animation is not available for this template. Select a template that supports animation (e.g. Floating Chat Icon, Guest Relations Assistant, or Premium Concierge).
            </div>
          )}
        </>
      ) : (
        renderContentEditor()
      )}
    </div>
  );
};

export default TemplateLibrary;
